/**
 * 앱스킴(딥링크) 환경 분기 유틸리티
 *
 * iOS/Android 앱이 Dev/Stage/Prod 환경으로 분리되면서 앱스킴도 환경별로 나뉘었다.
 * 웹은 자신의 배포 환경(호스트네임) 기준으로 스킴 prefix를 분기해 호출한다.
 *
 * | 호스트 첫 라벨        | prefix             |
 * | --------------------- | ------------------ |
 * | `-dev` 로 끝남        | macarongappdev     |
 * | `-stage` 로 끝남      | macarongappstage   |
 * | 그 외 (prod)          | macarongapp        |
 *
 * 예) service-dev.mycle.co.kr → macarongappdev:// , modoo-dev.macarong.net → macarongappdev://
 */

const APP_SCHEME_PROTOCOL = {
  PROD: 'macarongapp',
  STAGE: 'macarongappstage',
  DEV: 'macarongappdev',
} as const

/**
 * 파트너스 앱(정비소 사장님용) 스킴. 마이클 앱과 별개 앱이라 prefix 가 다르고,
 * 환경별 빌드도 각자 자기 스킴만 OS 에 등록한다.
 *
 * ⚠️ QR·외부 링크로 여는 딥링크는 OS 가 등록된 스킴으로만 라우팅한다.
 * dev 에서 prod 스킴을 쓰면 운영 앱이 열리거나(설치돼 있으면) 아무것도 안 열린다.
 */
const PARTNERS_APP_SCHEME_PROTOCOL = {
  PROD: 'macarongpartners',
  STAGE: 'macarongpartnersstage',
  DEV: 'macarongpartnersdev',
} as const

type TDeployEnv = 'PROD' | 'STAGE' | 'DEV'

/**
 * 호스트네임 첫 라벨로 배포 환경을 판정한다.
 * SSR 등 window 가 없으면 prod 로 본다 — 실제 스킴 호출은 모두 클라이언트에서 일어난다.
 */
const getDeployEnv = (): TDeployEnv => {
  if (typeof window === 'undefined') return 'PROD'

  const [firstLabel] = window.location.hostname.split('.')

  if (firstLabel.endsWith('-dev')) return 'DEV'
  if (firstLabel.endsWith('-stage')) return 'STAGE'
  return 'PROD'
}

/**
 * isSafeUrl 등 프로토콜 검증에서 재사용하는 전체 앱스킴 prefix 목록
 */
export const APP_SCHEME_PROTOCOLS = Object.values(APP_SCHEME_PROTOCOL)

/**
 * 현재 배포 환경(호스트네임) 기준 앱스킴 prefix 반환
 *
 * SSR 등 window가 없는 환경에서는 prod 스킴을 기본값으로 사용한다.
 * 실제 앱스킴 호출은 모두 클라이언트 런타임에서 일어난다.
 */
export const getAppSchemeProtocol = (): string =>
  APP_SCHEME_PROTOCOL[getDeployEnv()]

/**
 * 현재 배포 환경 기준 파트너스 앱스킴 prefix 반환
 */
export const getPartnersAppSchemeProtocol = (): string =>
  PARTNERS_APP_SCHEME_PROTOCOL[getDeployEnv()]

/**
 * 환경에 맞는 파트너스 앱스킴 URL 생성
 *
 * @param path `://` 뒤에 붙을 경로. 예) `web?url=...`, `maintenance-report?reservationId=1`
 * @returns 예) `macarongpartnersdev://web?url=...`
 */
export const buildPartnersAppScheme = (path: string): string =>
  `${getPartnersAppSchemeProtocol()}://${path}`

/**
 * 환경에 맞는 앱스킴 URL 생성
 *
 * @param path `/` 로 시작하는 경로 (쿼리 포함). 예) `/open/share?url=...`
 * @returns 예) `macarongappdev:///open/share?url=...`
 */
export const buildAppScheme = (path: string): string =>
  `${getAppSchemeProtocol()}://${path}`
